import React, { useState, useEffect, useMemo, useRef } from "react";
import { instance2 } from "../utils/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2Icon } from "lucide-react";
import { Textarea } from "../shadcn/textarea";
import { Button } from "../shadcn/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../shadcn/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../shadcn/select";
import { Input } from "../shadcn/input";
import { PaperAirplaneIcon } from "@heroicons/react/16/solid";
import { localDateString } from "src/lib/utils";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { toast } from "sonner";
import { useCallback } from "react";
import { OrderDetailsDisplayCard } from "./components/order-details-display-card";
import { useImageUploader } from "src/lib/hooks";
import { ImageUploadCarousel } from "./components/image-upload-carousel";
import { formatNumber } from "../numberingformat";

const useRatingParams = () => {
  const [params, setParams] = useState([]); // [{id: 1, name: "Warna"}, ...]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await instance2.get("/customers/result_rate_param");
      const data = res?.data?.data ?? res?.data ?? [];
      setParams(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || "Gagal memuat parameter penilaian",
      );
      setParams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { params, loading, error };
};

const useComplainData = (complainId, formSetValue) => {
  const [details, setDetails] = useState({
    customerName: "",
    kitchenName: "",
    schData: "",
    realData: "",
    receivedData: "",
    omprenghabis: "",
    omprengsisa: "",
    dateFormatted: "",
    menu: {
      karbohidrat: "-",
      protein: "-",
      sayuran: "-",
      buah: "-",
      minuman: "-",
    },
    menuIds: {
      karbohidrat: null,
      protein: null,
      sayuran: null,
      buah: null,
      minuman: null,
    }, // 👈 add this
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!complainId) {
      toast.error("ID tidak ditemukan untuk memuat data.");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const resultResponse = await instance2.get(
        `/customers_result/id/${complainId}`,
      );
      const resultData = resultResponse.data[0];

      if (resultData) {
        const date = new Date(resultData.date);
        const formattedDate = date.toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });

        let menuData = {
          karbohidrat: "-",
          protein: "-",
          sayuran: "-",
          buah: "-",
          minuman: "-",
        };
        let menuIds = {
          karbohidrat: null,
          protein: null,
          sayuran: null,
          buah: null,
          minuman: null,
        };

        try {
          const menuResponse = await instance2.get(
            `/menu_schedule/${resultData.location_id}/${localDateString(date)}/${localDateString(date)}`,
          );
          const fetchedMenu = menuResponse.data[0];
          if (fetchedMenu) {
            menuData = {
              karbohidrat: fetchedMenu.KARBOHIDRAT ?? "-",
              protein: fetchedMenu.PROTEIN ?? "-",
              sayuran: fetchedMenu.SAYURAN ?? "-",
              buah: fetchedMenu.BUAH ?? "-",
              minuman: fetchedMenu.MINUMAN ?? "-",
            };
            menuIds = {
              karbohidrat: fetchedMenu.k_id ?? null,
              protein: fetchedMenu.p_id ?? null,
              sayuran: fetchedMenu.s_id ?? null,
              buah: fetchedMenu.b_id ?? null,
              minuman: fetchedMenu.m_id ?? null,
            };
          }
        } catch (menuError) {
          console.error("Error fetching menu data:", menuError);
          toast.warn("Gagal memuat detail menu.");
        }
        setDetails({
          customerName: resultData.customer_name,
          kitchenName: resultData.kitchen_name,
          schData: resultData.qty_sch,
          realData: resultData.qty_real,
          omprenghabis: resultData.omprenghabis,
          omprengsisa: resultData.omprengsisa,
          dateFormatted: formattedDate,
          menu: menuData,
          menuIds, // 👈 keep dish ids here
        });

        formSetValue("id", complainId, { shouldValidate: true });
        formSetValue("received_by", resultData.received_by || "", {
          shouldValidate: true,
        });
        formSetValue("feedback", resultData.feedback || "", {
          shouldValidate: true,
        });
        formSetValue("rate_quality", resultData.rate_quality || "", {
          shouldValidate: true,
        });
      } else {
        toast.error("Data pesanan tidak ditemukan.");
      }
    } catch (error) {
      console.error("Error fetching complain data:", error);
      toast.error("Gagal memuat data pesanan.");
    } finally {
      setIsLoading(false);
    }
  }, [complainId, formSetValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { complainDetails: details, isLoadingDetails: isLoading };
};
// Map menu key -> category_id (replace these with your real IDs)
const CATEGORY_ID_BY_KEY = {
  karbohidrat: 1,
  protein: 2,
  sayuran: 3,
  buah: 4,
  minuman: 5,
};
const parseDigits = (s) => {
  const only = s.replace(/[^\d]/g, "");
  return only === "" ? undefined : Number(only);
};

const formSchema = z.object({
  id: z.string(),
  received_by: z.string().optional(),
  rate_quality: z.number().min(1).max(5), // ✅ overall
  warna: z.number().min(1).max(5).optional(),
  aroma: z.number().min(1).max(5).optional(),
  tekstur: z.number().min(1).max(5).optional(),
  rasa: z.number().min(1).max(5).optional(),
  kematangan: z.number().min(1).max(5).optional(),
  feedback: z.string().min(1, "Ulasan tidak boleh kosong"),
  file: z
    .array(z.instanceof(File))
    .min(1, { message: "Harap menambahkan setidaknya satu foto!" }),
    omprenghabis: z.coerce.number().min(0, "Tidak boleh kurang dari 0"),
    omprengsisa: z.coerce.number()
});

export default function UserComplain() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: id || "",
      received_by: "",
      rate_quality: undefined,
      warna: undefined,
      aroma: undefined,
      tekstur: undefined,
      rasa: undefined,
      kematangan: undefined,
      feedback: "",
      file: [],
      omprenghabis: 0,
      omprengsisa: 0,
    },
  });

  const {
    setValue,
    getValues,
    reset: resetForm,
    control,
    handleSubmit,
    formState,
  } = form;

  const { complainDetails, isLoadingDetails } = useComplainData(id, setValue);
  useEffect(() => {
  if (isLoadingDetails) return;

  // qty_real from server (raw number); default 0
  const realHabis = Number(complainDetails?.omprenghabis ?? 0) || Number(complainDetails?.realData ?? 0) || 0;

  // If user hasn't typed anything meaningful yet, set it
  const current = getValues("omprenghabis");
  if (current == null || current === 0) {
    setValue("omprenghabis", realHabis, { shouldDirty: false, shouldValidate: true });
  }
}, [isLoadingDetails, complainDetails?.omprenghabis, complainDetails?.realData, getValues, setValue]);

  useEffect(() => {
  if (isLoadingDetails) return;

  // qty_real from server (raw number); default 0
  const realSisa = Number(complainDetails?.omprengsisa ?? 0) || 0;

  // If user hasn't typed anything meaningful yet, set it
  const current = getValues("omprengsisa");
  if (current == null || current === 0) {
    setValue("omprengsisa", realSisa, { shouldDirty: false, shouldValidate: true });
  }
}, [isLoadingDetails, complainDetails?.omprengsisa, getValues, setValue]);


  const { selectedFilesData, handleFileChange, handleRemoveImage, resetFiles } =
    useImageUploader(setValue);

  const onSubmitHandler = async (values) => {
    toast.loading("Mengirimkan ulasan...", { id: "loadingSubmit" });
    setIsSubmitting(true);

    // 1) Main complain + images
    const formData = new FormData();
    formData.append("id", values.id);
    formData.append("feedback", values.feedback);
    formData.append("received_by", values.received_by || "");
    formData.append("rate_quality", String(values.rate_quality));
    formData.append("omprenghabis", String(values.omprenghabis));
    formData.append("omprengsisa", String(values.omprengsisa));
    values.file.forEach((file) => formData.append("images", file));
    console.log(Object.fromEntries(formData));

    try {
      const response = await instance2.post(
        "/customers_result/complain",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const mainOk =
        (typeof response.status === "number" &&
          response.status >= 200 &&
          response.status < 300) ||
        response?.status === "OK";

      if (!mainOk) {
        toast.dismiss("loadingSubmit");
        toast.error(response?.data?.message || "Gagal mengirimkan ulasan.");
        return;
      }

      // --- Second: submit each rating detail (bulk) ---
      const ratings = getValues("ratings") || {};
      const { menuIds } = complainDetails; // dish ids by menuKey

      const detailPayloads = [];
      for (const [menuKey, perParam] of Object.entries(ratings)) {
        const category_id = CATEGORY_ID_BY_KEY[menuKey];
        const dish_id = menuIds?.[menuKey] ?? null; // k_id/p_id/etc for that menu

        if (!category_id || !dish_id) continue;

        for (const [paramIdStr, value] of Object.entries(perParam || {})) {
          const param_id = Number(paramIdStr);
          if (!param_id || value == null) continue;

          detailPayloads.push({
            result_id: values.id, // complainId
            category_id,
            param_id,
            dish_id,
            value, // 1..5
          });
        }
      }

      if (detailPayloads.length) {
        try {
          // 🚀 Post as one array instead of many requests
          const res = await instance2.post(
            "/customers/result_rate_detail",
            detailPayloads,
          );

          if (
            !(res.status >= 200 && res.status < 300) &&
            res?.status !== "OK"
          ) {
            toast.warning(
              "Penilaian detail gagal dikirim. Coba lagi dari riwayat.",
            );
          }
        } catch (e) {
          console.error("Error posting detail ratings", e);
          toast.error("Gagal mengirim penilaian detail.");
        }
      }

      // 3) Done
      toast.dismiss("loadingSubmit");
      toast.success("Berhasil menambahkan ulasan!");
      resetForm({
        id: id || "",
        received_by: getValues("received_by"),
        rate_quality: undefined,
        feedback: "",
        file: [],
      });
      resetFiles();
      navigate(`/userfeedback?id=${values.id}`);
    } catch (error) {
      toast.dismiss("loadingSubmit");
      toast.error("Terjadi kesalahan server saat menambahkan ulasan!");
      if (error?.response) {
        toast.error(
          `Detail: ${error.response.data?.message || "Error tidak diketahui"}`,
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const cardMenuData = complainDetails.menu;
  const cardOrderData = {
    formattedDate: complainDetails.dateFormatted,
    customer_name: complainDetails.customerName,
    qty_sch: complainDetails.schData,
    qty_real: complainDetails.realData,
    hasSubmittedReport: !!complainDetails.receivedData,
    received_qty: complainDetails.receivedData,
    kitchen_name: complainDetails.kitchenName,
  };

  // helper: compact the menu list you actually want to rate

  // NEW: load the 5 rating params dynamically
  const { params: ratingParams, loading: isLoadingParams } = useRatingParams();

  // for the rating options label
  const RATING_DESCRIPTIONS = {
    1: "Buruk Sekali",
    2: "Buruk",
    3: "Sedang",
    4: "Baik",
    5: "Baik Sekali",
  };

  // compact the menu list you actually want to rate (names only, no *_id fields)
  const validMenus = useMemo(
    () =>
      Object.entries(cardMenuData).filter(
        ([, value]) => value !== null && value !== "-",
      ),
    [cardMenuData],
  );

  // when menu or params change, initialize default rating fields so selects are controlled
  useEffect(() => {
    if (!ratingParams.length || !validMenus.length) return;

    const defaults = {};
    for (const [menuKey] of validMenus) {
      // one record per menu item
      if (!defaults.ratings) defaults.ratings = {};
      defaults.ratings[menuKey] = {};
      for (const p of ratingParams) {
        // initialize as undefined (no selection) or null if you prefer
        defaults.ratings[menuKey][p.id] = undefined;
      }
    }
    // only set fields that are not set yet (avoid wiping when user typed something)
    // merge strategy: read current, then fill blanks
    const current = getValues();
    const merged = { ...current, ratings: { ...(current.ratings || {}) } };
    for (const [menuKey] of validMenus) {
      merged.ratings[menuKey] = { ...(merged.ratings[menuKey] || {}) };
      for (const p of ratingParams) {
        if (merged.ratings[menuKey][p.id] === undefined) {
          merged.ratings[menuKey][p.id] = defaults.ratings[menuKey][p.id];
        }
      }
    }
    // update form state silently (no validation spam)
    setValue("ratings", merged.ratings, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [validMenus, ratingParams, getValues, setValue]);

  const combinedIsLoading = isSubmitting || isLoadingDetails || isLoadingParams;

  // tiny helper component to avoid repeating Select boilerplate
  function RatingSelect({ control, name, label, disabled }) {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem className="mx-auto max-w-[64px]">
            {/* Hide label when used in table */}
            {label ? <FormLabel>{label}</FormLabel> : null}
            <Select
              value={
                field.value === undefined || field.value === null
                  ? undefined
                  : field.value.toString()
              }
              onValueChange={(value) => field.onChange(Number(value))}
              disabled={disabled}
            >
              <FormControl>
                <SelectTrigger className="h-8">
                  {field.value ? (
                    <SelectValue />
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((rate) => (
                  <SelectItem key={rate} value={String(rate)}>
                    {rate}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // Prepopulate ratings from server (result_rate_detail)
  useEffect(() => {
    if (!id || !ratingParams.length) return; // need params first
    if (!complainDetails?.menuIds) return; // need dish ids mapping
    if (!complainDetails?.menu) return;

    (async () => {
      try {
        const res = await instance2.get("/customers/result_rate_detail", {
          params: { param: "result_id", id },
        });

        let rows = res?.data || [];
        if (!Array.isArray(rows) || rows.length === 0) return;

        // ensure stable order so "last wins" is deterministic
        rows = rows.sort((a, b) => a.id - b.id);

        // Map: category_id -> menuKey (only for menus currently visible)
        const categoryIdToKey = {};
        for (const [menuKey, val] of Object.entries(complainDetails.menu)) {
          if (val === "-" || val == null) continue;
          const cid = CATEGORY_ID_BY_KEY[menuKey];
          if (cid) categoryIdToKey[cid] = menuKey;
        }

        // Build a flat patch of field paths -> value
        // e.g. { "ratings.karbohidrat.1": 3, "ratings.protein.4": 2, ... }
        const patch = {};
        for (const r of rows) {
          const menuKey = categoryIdToKey[r.category_id];
          if (!menuKey) continue;

          const expectedDishId = complainDetails.menuIds?.[menuKey];
          if (expectedDishId && r.dish_id && r.dish_id !== expectedDishId) {
            // skip ratings that belong to a different dish/day
            continue;
          }

          const path = `ratings.${menuKey}.${r.param_id}`;
          patch[path] = r.value;
        }

        const paths = Object.keys(patch);
        if (!paths.length) return;

        // IMPORTANT: set each field path (Controller-aware)
        for (const p of paths) {
          setValue(p, patch[p], { shouldValidate: false, shouldDirty: false });
        }
        // (optional) trigger validation after prefilling
        // await trigger(paths);
      } catch (e) {
        console.error("Failed to prefill ratings:", e);
        toast.warn("Gagal memuat penilaian sebelumnya.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ratingParams, complainDetails.menu, complainDetails.menuIds]);

  // Prevent double prefill if component re-renders
  const hasPrefilledImagesRef = useRef(false);
  const PREFILL_ONCE_CACHE = useMemo(() => new Set(), []);
  useEffect(() => {
    if (!id) return;

    // ✅ guard against dev StrictMode double-mount & repeated navigations
    if (PREFILL_ONCE_CACHE.has(id)) return;

    // if user already added files, don't override
    const existing = getValues("file") || [];
    if (existing.length > 0) return;

    PREFILL_ONCE_CACHE.add(id);

    (async () => {
      try {
        // 1) fetch feedback rows
        const res = await instance2.get(`/customers_review/feedback/${id}`);
        let rows = (res?.data?.data ?? res?.data ?? []).filter(
          (r) => !!r?.image,
        );

        // 2) dedupe by image path (if API accidentally returns the same row twice)
        rows = rows.filter(
          (r, i, arr) => arr.findIndex((x) => x.image === r.image) === i,
        );
        if (rows.length === 0) return;

        // 3) download each image as blob (robust to interceptors that unwrap)
        const blobs = await Promise.all(
          rows.map(async (row) => {
            const r = await instance2.get(row.image, {
              responseType: "blob",
              transformResponse: [(d) => d], // leave as raw blob
            });
            return r?.data ?? r; // if interceptor returns Blob directly
          }),
        );

        // 4) build File[] (dedupe by name+size just in case)
        const files = blobs.map((blob, idx) => {
          const type = blob?.type || "image/*";
          const extFromType = type.split("/")[1];
          const extFromPath = rows[idx].image.split(".").pop();
          const ext = extFromType || extFromPath || "png"; // <- remove "|| 'jpg'"
          const name = `feedback-${rows[idx].feedback_id ?? rows[idx].id ?? idx}.${ext}`;
          return new File([blob], name, { type });
        });

        // 5) Replace, don't append (prevents doubling)
        resetFiles?.(); // from your hook
        setValue("file", [], { shouldValidate: false, shouldDirty: false });

        const dt = new DataTransfer();
        files.forEach((f) => dt.items.add(f));
        handleFileChange({ target: { files: dt.files } }); // your hook event
      } catch (e) {
        console.error("Failed to prepopulate feedback images:", e);
        // let user continue manually
      }
    })();
  }, [
    id,
    getValues,
    handleFileChange,
    resetFiles,
    setValue,
    PREFILL_ONCE_CACHE,
  ]);

  return (
    <div className="flex flex-col mx-auto justify-center items-center gap-6 w-full min-h-[100dvh] py-12 px-8 max-w-5xl">
      <div className="flex flex-col gap-1 w-full">
        <h1 className="text-2xl font-semibold text-balance text-center sm:text-left w-full">
          Ulasan Kualitas Makanan
        </h1>
        <h3 className="text-balance text-neutral-500 text-center sm:text-[1rem] text-sm sm:text-left">
          Berikan ulasan untuk makanan yang Anda terima
        </h3>
      </div>

      <div className="flex flex-col w-full gap-3">
        {isLoadingDetails ? (
          <div className="flex justify-center items-center p-10">
            <Loader2Icon className="animate-spin h-10 w-10 text-merah-hover" />
          </div>
        ) : (
          <OrderDetailsDisplayCard
            orderData={cardOrderData}
            menuData={cardMenuData}
          />
        )}
      </div>

      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmitHandler)}
          className="grid grid-rows-[auto_1fr] gap-y-6 w-full"
        >
          <div className="grid lg:grid-rows-2 gap-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-6 w-full h-full">
              {/* LEFT: ratings -> ulasan (2/3 width on lg) */}
              <div className="grid grid-rows-[auto_1fr] overflow-hidden gap-y-4 px-1 pb-1 lg:col-span-2">           
                {/* Ompreng Habis / Sisa */}
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             <FormField
  control={control}
  name="omprenghabis"
  render={({ field }) => (
    <FormItem>
      <FormLabel>Jumlah ompreng yang habis</FormLabel>
      <FormControl>
        <Input
          inputMode="numeric"
          type="text"
          // UI shows formatted
          value={
            field.value === undefined || field.value === null || field.value === ""
              ? ""
              : formatNumber(field.value)
          }
          // But we store raw number
          onChange={(e) => {
            const n = parseDigits(e.target.value);
            field.onChange(n);
          }}
          onBlur={field.onBlur}
        />
      </FormControl>
      <FormMessage />
    </FormItem>
  )}
/>

<FormField
  control={control}
  name="omprengsisa"
  render={({ field }) => (
    <FormItem>
      <FormLabel>Jumlah ompreng yang tersisa</FormLabel>
      <FormControl>
        <Input
          inputMode="numeric"
          type="text"
          value={
            field.value === undefined || field.value === null || field.value === ""
              ? ""
              : formatNumber(field.value)
          }
          onChange={(e) => {
            const n = parseDigits(e.target.value);
            field.onChange(n);
          }}
          onBlur={field.onBlur}
        />
      </FormControl>
      <FormMessage />
    </FormItem>
  )}
/>

           </div>
                <FormField
                  control={control}
                  name="rate_quality"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Kualitas Makanan</FormLabel>
                      <Select
                        value={field.value?.toString()}
                        onValueChange={(value) => field.onChange(Number(value))}
                        disabled={combinedIsLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            {field.value ? (
                              <SelectValue />
                            ) : (
                              <span className="text-muted-foreground">
                                Pilih rating
                              </span>
                            )}
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {[1, 2, 3, 4, 5].map((rate) => (
                            <SelectItem key={rate} value={String(rate)}>
                              {rate} - {RATING_DESCRIPTIONS[rate]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Ratings per menu item */}
                {/* Ratings per menu item (as a table) */}
                <div className="mt-4 overflow-x-auto rounded-lg border">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-neutral-50">
                      <tr className="text-sm">
                        <th className="border-b px-3 py-2 text-left font-semibold w-56">
                          Menu
                        </th>
                        {ratingParams.length ? (
                          ratingParams.map((p) => (
                            <th
                              key={p.id}
                              className="border-b px-2 py-2 text-center font-semibold"
                            >
                              {p.name}
                            </th>
                          ))
                        ) : (
                          <th className="border-b px-2 py-2 text-center font-semibold">
                            Parameter penilaian belum tersedia
                          </th>
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {validMenus.length ? (
                        validMenus.map(([menuKey, menuName]) => (
                          <tr key={`row-${menuKey}`} className="align-top">
                            {/* Menu name */}
                            <td className="border-t px-3 py-3">
                              <div className="font-medium">{menuName}</div>
                              <div className="text-xs text-neutral-500 capitalize">
                                {menuKey}
                              </div>
                            </td>

                            {/* One RatingSelect per parameter */}
                            {ratingParams.map((p) => (
                              <td
                                key={`${menuKey}-${p.id}`}
                                className="border-t px-2 py-2 text-center"
                              >
                                <RatingSelect
                                  control={control}
                                  name={`ratings.${menuKey}.${p.id}`}
                                  label={null /* hide label inside table */}
                                  disabled={combinedIsLoading}
                                />
                              </td>
                            ))}

                            {/* If there are no params, make one empty cell spanning 1 col */}
                            {!ratingParams.length && (
                              <td className="border-t px-2 py-2 text-center text-sm text-neutral-500">
                                —
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            className="border-t px-3 py-4 text-center text-sm text-neutral-600"
                            colSpan={Math.max(1, ratingParams.length + 1)}
                          >
                            Tidak ada menu yang dapat dinilai.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <ImageUploadCarousel
                isLoading={combinedIsLoading}
                filesData={selectedFilesData}
                onFileChange={handleFileChange}
                onRemoveImage={handleRemoveImage}
                formControl={control}
                name="file"
              />
            </div>



            <FormField
              control={control}
              name="feedback"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Saran dan masukan</FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-80"
                      placeholder="Sampaikan saran dan masukan Anda..."
                      {...field}
                      disabled={combinedIsLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex items-end gap-4 justify-between pt-4">
            <div className="hidden lg:flex items-center gap-2">
              <span className="text-xs">
                &copy; {new Date().getFullYear()} | Dapur Merdeka
              </span>
            </div>
            <Button
              disabled={
                isSubmitting ||
                isLoadingDetails ||
                (!formState.isValid && formState.isSubmitted)
              }
              className="w-full lg:w-auto"
              type="submit"
            >
              {isSubmitting ? (
                <Loader2Icon className="animate-spin mr-2" />
              ) : (
                <PaperAirplaneIcon className="h-5 w-5 mr-2" />
              )}{" "}
              Kirim Ulasan
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
